import type { AuthSession, LoginInput, Me, PermissionCode } from '@pm/schemas';
import { useTheme } from '@pm/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from './api';

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; session: AuthSession; me: Me };

interface AuthContextValue {
  state: AuthState;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  selectOrganization: (organizationId: string) => Promise<void>;
  refetchMe: () => Promise<void>;
  can: (code: PermissionCode) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

type TimerRef = { current: number | null };

/** Agenda refresh proativo um pouco antes do access token expirar (e se reagenda). */
function scheduleRefreshLoop(timer: TimerRef, expiresIn: number): void {
  if (timer.current) window.clearTimeout(timer.current);
  const delay = Math.max(15_000, (expiresIn - 30) * 1000);
  timer.current = window.setTimeout(() => {
    void api.refresh().then((s) => s && scheduleRefreshLoop(timer, s.expires_in));
  }, delay);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const theme = useTheme();
  const queryClient = useQueryClient();
  const refreshTimer = useRef<number | null>(null);

  const scheduleRefresh = useCallback(
    (expiresIn: number) => scheduleRefreshLoop(refreshTimer, expiresIn),
    [],
  );

  const hydrate = useCallback(
    async (session: AuthSession) => {
      api.setAccessToken(session.access_token);
      const me = await api.get<Me>('/auth/me');
      theme.replace(me.appearance);
      setState({ status: 'authenticated', session, me });
      scheduleRefresh(session.expires_in);
    },
    [scheduleRefresh, theme],
  );

  const clear = useCallback(() => {
    api.setAccessToken(null);
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    queryClient.clear();
    setState({ status: 'anonymous' });
  }, [queryClient]);

  // Boot: tenta renovar pelo cookie.
  useEffect(() => {
    api.setUnauthenticatedHandler(clear);
    let cancelled = false;
    void api.refresh().then(async (session) => {
      if (cancelled) return;
      if (!session) return setState({ status: 'anonymous' });
      try {
        await hydrate(session);
      } catch {
        clear();
      }
    });
    return () => {
      cancelled = true;
      api.setUnauthenticatedHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (input: LoginInput) => {
      const session = await api.post<AuthSession>('/auth/login', input, { noAuthRetry: true });
      await hydrate(session);
    },
    [hydrate],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { noAuthRetry: true });
    } finally {
      clear();
    }
  }, [clear]);

  const selectOrganization = useCallback(
    async (organizationId: string) => {
      const session = await api.post<AuthSession>('/auth/select-organization', {
        organization_id: organizationId,
      });
      queryClient.clear();
      await hydrate(session);
    },
    [hydrate, queryClient],
  );

  const refetchMe = useCallback(async () => {
    if (state.status !== 'authenticated') return;
    const me = await api.get<Me>('/auth/me');
    setState({ ...state, me });
  }, [state]);

  const can = useCallback(
    (code: PermissionCode) =>
      state.status === 'authenticated' && state.me.permissions.includes(code),
    [state],
  );

  const value = useMemo(
    () => ({ state, login, logout, selectOrganization, refetchMe, can }),
    [state, login, logout, selectOrganization, refetchMe, can],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa de <AuthProvider>');
  return ctx;
}

/** Atalho para telas que só renderizam autenticadas (dentro de RequireAuth). */
export function useMe(): Me {
  const { state } = useAuth();
  if (state.status !== 'authenticated') throw new Error('useMe fora de área autenticada');
  return state.me;
}
